import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';
import { playerValuationRefreshed } from '../../../state/app/players/actions';
import type { Player, PlayerValuation } from '../../../state/app/players/types';
import './ValuationRefresh.css';

export default function ValuationRefresh({
  player,
}: {
  player: Pick<Player, 'id' | 'valuation_at' | 'can_refresh_valuation'>;
}) {
  const dispatch = useDispatch();
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  if (!player.can_refresh_valuation) return null;

  const refresh = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setFailed(false);
    try {
      const response = await apiCaller({
        apiUrl: API_URL,
        path: `/players/${player.id}/refresh_valuation`,
        method: 'POST',
        data: {},
      });
      if (response.status !== 200) throw new Error('Valuation failed');
      dispatch(playerValuationRefreshed(response.result as PlayerValuation));
    } catch {
      setFailed(true);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  const stamp = player.valuation_at
    ? new Date(player.valuation_at).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <button
      type="button"
      className="valuation-refresh"
      disabled={busy}
      aria-label="Refresh valuation"
      aria-busy={busy}
      title={
        failed
          ? 'Refresh failed. Please wait a minute, then retry.'
          : player.valuation_at
            ? `Valued ${new Date(player.valuation_at).toLocaleString()}. Click to refresh.`
            : 'Refresh valuation'
      }
      onClick={event => {
        event.stopPropagation();
        void refresh();
      }}
    >
      {busy
        ? 'Valuing…'
        : stamp
          ? `Valued ${stamp}`
          : 'Valuation time unavailable'}
      <span className="valuation-refresh-affordance" aria-hidden="true">
        {' '}
        ↻
      </span>
      {failed && <span role="status"> · retry</span>}
    </button>
  );
}
